import { ia, iaRa, io } from 'lib/helpers/utility';

import {
  getAllDateOptions,
  getDateRange,
  SUGGESTION_ID_RE
} from 'components/shared/DateRangeSelector/lib/helpers';
import {
  anchorRangeToZone,
  isCalendarDay,
  toCalendarDay
} from 'components/shared/DateRangeSelector/lib/dayBoundaries';

const ABSOLUTE_RANGE_IDS = ['all-time', 'custom-date-range'];

// Before typed suggestions minted an id, the picker persisted their label as one ('Yesterday',
// 'Last 3 Weeks'), which matches no preset — so those saved filters are re-resolved from the label.
const LEGACY_SUGGESTION_RE =
  /^(?:(yesterday|tomorrow)|(last|next) (?:(\d+) )?(day|week|month)s?)$/i;

const resolveLegacySuggestion = (id, timeZone) => {
  const legacy = typeof id === 'string' && LEGACY_SUGGESTION_RE.exec(id.trim());
  if (!legacy) return null;

  const [, single, relative, amount, unit] = legacy;
  const isFuture = /^(tomorrow|next)$/i.test(single || relative);

  return getDateRange(
    `${(unit || 'day').toLowerCase()}s`,
    Number(amount) || 1,
    timeZone,
    isFuture ? 'future' : 'past'
  );
};

// Builds `id -> range` lookups lazily, one per timezone, shared across every filter in the same call.
// getAllDateOptions resolves ~24 ranges through date-fns and the tz database, which is wasted work
// when all a filter needs is one id.
const createRangeLookup = () => {
  const byTimezone = new Map();

  return (timezone) => {
    let byId = byTimezone.get(timezone);
    if (!byId) {
      byId = new Map(getAllDateOptions(timezone).map((option) => [option.id, option.value]));
      byTimezone.set(timezone, byId);
    }
    return byId;
  };
};

// A relative range ("Today", "Last 30 days") is stored as the instants it resolved to when it was
// picked, while the chip keeps rendering its label from the id — so a tab left open overnight, or a
// saved view, silently queries the wrong period under a label that says otherwise. Re-resolve it at
// request time, in the zone it was created for, so the label and the data always agree.
const resolveRelativeRange = (values, timezone, rangesFor) => {
  if (!values?.startDate || !values?.endDate) return values;
  if (!values?.id || ABSOLUTE_RANGE_IDS.includes(values.id)) {
    return anchorRangeToZone(values, timezone);
  }

  // A date-only filter stays date-only: its preset is re-resolved in the caller's zone like any other,
  // then collapsed back to the calendar days its DATE column compares against.
  const isDateOnly = isCalendarDay(values.startDate) && isCalendarDay(values.endDate);

  // Prefer the zone the range was picked in; fall back to the caller's for ranges built before the
  // picker stamped one (report defaults, older saved views).
  const rangeTimezone = values.timezone || timezone;

  const suggestion = SUGGESTION_ID_RE.exec(values.id);
  const [, direction, amount, unit] = suggestion || [];

  const resolved = suggestion
    ? getDateRange(unit, Number(amount), rangeTimezone, direction)
    : rangesFor(rangeTimezone).get(values.id) || resolveLegacySuggestion(values.id, rangeTimezone);

  if (!resolved) return values;

  if (isDateOnly) {
    return {
      ...values,
      startDate: toCalendarDay(resolved.startDate, rangeTimezone),
      endDate: toCalendarDay(resolved.endDate, rangeTimezone)
    };
  }

  return { ...values, timezone: rangeTimezone, ...resolved };
};

export const createQueryFilters = (filters, view, timezone) => {
  const result = {};
  const rangesFor = createRangeLookup();

  if (view) {
    result.status = [{ values: [view], formula: 'is' }];
  }

  if (!io(filters)) return result;

  Object.entries(filters).forEach(([key, value]) => {
    if (!ia(value)) return;
    const mappedFilters = iaRa(value);

    // Search filter: pass through searchTerm and fields directly
    if (key === 'search') {
      const searchItem = mappedFilters[0];
      const searchTerm = searchItem?.values?.searchTerm?.trim();
      if (searchTerm) {
        result[key] = { ...searchItem.values, searchTerm };
      }
      return;
    }

    const filteredItems = mappedFilters
      .map((item) => {
        if (!item.values && typeof item.values !== 'number') return null;

        if (ia(item.values)) {
          return {
            formula: item.formula || 'is',
            values: item.values.map((v) => v.value)
          };
        }

        return {
          formula: item.formula,
          values: resolveRelativeRange(item.values, timezone, rangesFor)
        };
      })
      .filter(Boolean);

    if (filteredItems.length > 0) {
      result[key] = filteredItems;
    }
  });

  return result;
};
